import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  configureAuth,
  signIn as apiSignIn,
  signOut as apiSignOut,
  getCurrentUser,
  isAuthenticated,
  completeNewPassword as apiCompleteNewPassword,
  signInWithProvider,
  type AuthUser,
} from '@acitra/api-client';
import type { TenantWithRole, TenantUserRole } from '@acitra/shared';
import { loadConfig } from '../config';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  needsNewPassword: boolean;
  // Multi-tenant support
  tenants: TenantWithRole[];
  currentTenantId: string | null;
}

interface AuthActions {
  initialize: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<boolean>;
  loginWithIntuit: () => void;
  logout: () => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<boolean>;
  clearError: () => void;
  // Multi-tenant support
  fetchUserTenants: () => Promise<void>;
  selectTenant: (tenantId: string) => void;
  getCurrentTenant: () => TenantWithRole | null;
  getCurrentUserRole: () => TenantUserRole | null;
  // Connected tenant helpers (filters out disconnected tenants)
  getConnectedTenants: () => TenantWithRole[];
  hasConnectedTenant: () => boolean;
}

type AuthStore = AuthState & AuthActions;

let authConfigured = false;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      needsNewPassword: false,
      tenants: [],
      currentTenantId: null,

      // Initialize - load config, configure auth, check if user is authenticated
      initialize: async () => {
        try {
          set({ isLoading: true, error: null });

          console.log('[auth-store] Starting initialize...');

          // Load runtime config and configure auth (only once)
          if (!authConfigured) {
            console.log('[auth-store] Loading config...');
            const config = await loadConfig();
            console.log('[auth-store] Config loaded:', {
              hasUserPoolId: !!config.cognitoUserPoolId,
              hasClientId: !!config.cognitoClientId,
              hasDomain: !!config.cognitoDomain,
            });

            if (config.cognitoUserPoolId && config.cognitoClientId) {
              // Get the current URL for redirect
              const currentUrl = window.location.origin;

              console.log('[auth-store] Configuring auth with OAuth...');
              configureAuth({
                userPoolId: config.cognitoUserPoolId,
                userPoolClientId: config.cognitoClientId,
                oauth: config.cognitoDomain
                  ? {
                      domain: config.cognitoDomain,
                      scopes: ['openid', 'email', 'profile'],
                      redirectSignIn: [`${currentUrl}/auth/callback`],
                      redirectSignOut: [currentUrl],
                      responseType: 'code',
                    }
                  : undefined,
              });
              authConfigured = true;
              console.log('[auth-store] Auth configured');
            } else {
              // No auth config available
              console.log('[auth-store] No auth config, setting unauthenticated');
              set({ user: null, isAuthenticated: false, isLoading: false });
              return;
            }
          }

          console.log('[auth-store] Checking if authenticated...');
          const authenticated = await isAuthenticated();
          console.log('[auth-store] isAuthenticated result:', authenticated);

          if (authenticated) {
            console.log('[auth-store] Getting current user...');
            const user = await getCurrentUser();
            console.log('[auth-store] Current user:', user);

            // Fetch tenants first (before setting isAuthenticated)
            // This ensures tenants are available when navigation logic runs
            console.log('[auth-store] Fetching tenants...');
            await get().fetchUserTenants();
            console.log('[auth-store] Tenants fetched');

            // Set isAuthenticated and isLoading together after tenants are fetched
            set({ user, isAuthenticated: true, isLoading: false });
          } else {
            console.log('[auth-store] Not authenticated, setting state');
            set({ user: null, isAuthenticated: false, isLoading: false });
          }
        } catch (error) {
          console.error('[auth-store] Initialize error:', error);
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      // Login with email and password
      loginWithEmail: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null, needsNewPassword: false });

          const result = await apiSignIn(email, password);

          // Check if user needs to set new password (first login)
          if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            set({ isLoading: false, needsNewPassword: true });
            return false;
          }

          if (result.isSignedIn) {
            const user = await getCurrentUser();

            // Fetch tenants first (before setting isAuthenticated)
            // This ensures tenants are available when Login.tsx navigation logic runs
            await get().fetchUserTenants();

            // Set isAuthenticated and isLoading together after tenants are fetched
            set({ user, isAuthenticated: true, isLoading: false });
            return true;
          }

          set({ isLoading: false });
          return false;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      // Login with Intuit (federated sign-in)
      loginWithIntuit: () => {
        // This will redirect to Intuit login page
        signInWithProvider('Intuit');
      },

      // Complete new password challenge (first login)
      completeNewPassword: async (newPassword: string) => {
        try {
          set({ isLoading: true, error: null });

          const result = await apiCompleteNewPassword(newPassword);

          if (result.isSignedIn) {
            const user = await getCurrentUser();

            // Fetch tenants first (before setting isAuthenticated)
            // This ensures tenants are available when Login.tsx navigation logic runs
            await get().fetchUserTenants();

            // Set isAuthenticated and isLoading together after tenants are fetched
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              needsNewPassword: false,
            });
            return true;
          }

          set({ isLoading: false });
          return false;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Password change failed';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      // Logout
      logout: async () => {
        try {
          await apiSignOut();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            error: null,
            needsNewPassword: false,
            tenants: [],
            currentTenantId: null,
          });
        }
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Fetch user's tenants from API
      fetchUserTenants: async () => {
        try {
          const { listTenants } = await import('../services/api');
          const tenants = await listTenants();
          set({ tenants });

          // Auto-select tenant if user only has one connected tenant
          const connectedTenants = tenants.filter((t) => !t.quickbooks?.disconnectedAt);
          if (connectedTenants.length === 1) {
            set({ currentTenantId: connectedTenants[0].id });
          }
        } catch (error) {
          console.error('Failed to fetch tenants:', error);
          // Set empty array on error
          set({ tenants: [] });
        }
      },

      // Select a tenant
      selectTenant: (tenantId: string) => {
        const { tenants } = get();
        const tenant = tenants.find((t) => t.id === tenantId);
        if (tenant) {
          set({ currentTenantId: tenantId });
        }
      },

      // Get the currently selected tenant
      getCurrentTenant: () => {
        const { tenants, currentTenantId } = get();
        if (!currentTenantId) return null;
        return tenants.find((t) => t.id === currentTenantId) || null;
      },

      // Get the current user's role in the selected tenant
      getCurrentUserRole: () => {
        const { tenants, currentTenantId } = get();
        if (!currentTenantId) return null;
        const tenant = tenants.find((t) => t.id === currentTenantId);
        return tenant?.role || null;
      },

      // Get only tenants with active QB connections (not disconnected)
      getConnectedTenants: () => {
        const { tenants } = get();
        return tenants.filter((t) => !t.quickbooks?.disconnectedAt);
      },

      // Check if user has at least one connected tenant
      hasConnectedTenant: () => {
        const { tenants } = get();
        return tenants.some((t) => !t.quickbooks?.disconnectedAt);
      },
    }),
    {
      name: 'acitra-client-auth',
      // Only persist tenant selection, not auth state
      partialize: (state) => ({
        currentTenantId: state.currentTenantId,
      }),
    }
  )
);
